import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '@/contexts/CartContext';
import api from '@/lib/api';
import { Sparkles, ShoppingBag, Loader2, Check, Plus } from 'lucide-react';

export default function BundleWidget({ bundles, formatPrice }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [addingId, setAddingId] = useState(null);
  const [addedId, setAddedId] = useState(null);
  const [productData, setProductData] = useState({});
  const [checkedItems, setCheckedItems] = useState({});
  const [selectedBundleVariants, setSelectedBundleVariants] = useState({});

  useEffect(() => {
    if (!bundles?.length) return;
    const handles = new Set();
    bundles.forEach(b => b.products?.forEach(p => handles.add(p.product_handle)));
    handles.forEach(h => {
      api.get(`/products/${h}`).then(r => {
        setProductData(prev => ({ ...prev, [h]: r.data }));
      }).catch(() => {});
    });
    const initial = {};
    bundles.forEach(b => b.products?.forEach(p => { initial[`${b.id}-${p.product_handle}`] = true; }));
    setCheckedItems(initial);
  }, [bundles]);

  if (!bundles?.length) return null;

  const toggleItem = (bundleId, handle, isCurrentProduct) => {
    if (isCurrentProduct) return;
    setCheckedItems(prev => ({ ...prev, [`${bundleId}-${handle}`]: !prev[`${bundleId}-${handle}`] }));
  };

  const selectBundleVariant = (bundleId, handle, variantId) => {
    setSelectedBundleVariants(prev => ({ ...prev, [`${bundleId}-${handle}`]: variantId }));
  };

  const handleAddBundle = async (bundle) => {
    setAddingId(bundle.id);
    try {
      const itemsToAdd = bundle.products.filter(p => checkedItems[`${bundle.id}-${p.product_handle}`]);
      for (const p of itemsToAdd) {
        const pd = productData[p.product_handle];
        const selectedVarId = selectedBundleVariants[`${bundle.id}-${p.product_handle}`];
        const variant = selectedVarId && pd?.variants ? pd.variants.find(v => v.id === selectedVarId) : null;
        await addItem({ handle: p.product_handle, title: p.product_title }, variant, p.quantity || 1, bundle.id);
      }
      setAddingId(null);
      setAddedId(bundle.id);
      setTimeout(() => {
        setAddedId(null);
        navigate('/checkout');
      }, 800);
    } catch {
      setAddingId(null);
    }
  };

  return (
    <div data-testid="bundle-widget" className="space-y-5 mt-6 pt-6 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      {bundles.map(bundle => {
        const discountPct = bundle.discount_type === 'percentage' ? bundle.discount_value : 0;
        const checkedProducts = bundle.products?.filter(p => checkedItems[`${bundle.id}-${p.product_handle}`]) || [];
        const originalTotal = checkedProducts.reduce((sum, p) => {
          const pd = productData[p.product_handle];
          const selectedVarId = selectedBundleVariants[`${bundle.id}-${p.product_handle}`];
          const variant = selectedVarId && pd?.variants ? pd.variants.find(v => v.id === selectedVarId) : null;
          const price = variant?.price || pd?.price || p.price || 0;
          return sum + price * (p.quantity || 1);
        }, 0);
        const bundlePrice = discountPct > 0 ? originalTotal * (1 - discountPct / 100) : originalTotal;
        const savings = originalTotal - bundlePrice;
        const isAdding = addingId === bundle.id;
        const isAdded = addedId === bundle.id;

        // Check if all checked products with variants have a variant selected
        const allVariantsSelected = checkedProducts.every(p => {
          const pd = productData[p.product_handle];
          if (!pd?.options?.length || !pd?.variants?.length) return true;
          return !!selectedBundleVariants[`${bundle.id}-${p.product_handle}`];
        });

        return (
          <motion.div key={bundle.id} data-testid={`bundle-${bundle.id}`}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="p-5 rounded-lg" style={{ backgroundColor: '#F9F9F7', border: '1px solid rgba(0,0,0,0.06)' }}>

            <div className="flex items-center gap-2 mb-5">
              <Sparkles size={15} style={{ color: '#000' }} />
              <h3 className="font-sans text-xs font-bold uppercase tracking-[0.15em]" style={{ color: '#000' }}>Frequently Bought Together</h3>
            </div>

            <div className="flex items-center justify-center gap-2 mb-5 flex-wrap">
              {bundle.products?.map((p, i) => {
                const pd = productData[p.product_handle];
                const img = pd?.images?.[0]?.url;
                const isChecked = checkedItems[`${bundle.id}-${p.product_handle}`];
                const isFirst = i === 0;
                const hasVariants = pd?.options?.length > 0 && pd?.variants?.length > 0;
                const selectedVarId = selectedBundleVariants[`${bundle.id}-${p.product_handle}`];

                return (
                  <div key={p.product_handle} className="flex items-center gap-2">
                    {i > 0 && <Plus size={16} style={{ color: '#ccc' }} className="flex-shrink-0" />}
                    <div className="flex flex-col items-center" style={{ width: 100 }}>
                      <div className={`w-[88px] h-[88px] rounded-lg overflow-hidden border-2 mb-2 transition-all ${isChecked ? '' : 'opacity-30'}`}
                        style={{ borderColor: isChecked ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.04)', backgroundColor: '#fff' }}>
                        {img ? <img src={img} alt={p.product_title} className="w-full h-full object-cover" /> :
                          <div className="w-full h-full flex items-center justify-center font-sans text-[10px]" style={{ color: '#ccc' }}>No img</div>}
                      </div>
                      <p className="font-sans text-[11px] text-center leading-tight mb-1 line-clamp-2" style={{ color: isChecked ? '#333' : '#999' }}>
                        {p.product_title}
                      </p>
                      {/* Variant selector for bundle product */}
                      {hasVariants && isChecked && (
                        <select
                          value={selectedVarId || ''}
                          onChange={e => selectBundleVariant(bundle.id, p.product_handle, e.target.value)}
                          className="w-full text-[10px] font-sans border rounded px-1 py-0.5 mb-1"
                          style={{ borderColor: selectedVarId ? 'rgba(0,0,0,0.15)' : '#ef4444', color: '#333', fontSize: 10 }}>
                          <option value="">Select {pd.options[0]?.name || 'option'}</option>
                          {pd.variants.map(v => (
                            <option key={v.id} value={v.id}>{v.title}</option>
                          ))}
                        </select>
                      )}
                      <p className="font-sans text-xs font-semibold" style={{ color: isChecked ? '#000' : '#999' }}>
                        {formatPrice((() => {
                          const variant = selectedVarId && pd?.variants ? pd.variants.find(v => v.id === selectedVarId) : null;
                          return variant?.price || pd?.price || p.price || 0;
                        })())}
                      </p>
                      <label className="flex items-center gap-1.5 mt-1.5 cursor-pointer">
                        <input type="checkbox" checked={isChecked} onChange={() => toggleItem(bundle.id, p.product_handle, isFirst)}
                          disabled={isFirst} className="w-3.5 h-3.5 rounded border-gray-300 accent-black" />
                        <span className="font-sans text-[10px]" style={{ color: '#888' }}>{isFirst ? 'This item' : 'Add'}</span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-center gap-3 mb-4 flex-wrap">
              {savings > 0 && <span className="font-sans text-sm line-through" style={{ color: '#999' }}>{formatPrice(originalTotal)}</span>}
              <span className="font-sans text-lg font-bold" style={{ color: '#000' }}>{formatPrice(bundlePrice)}</span>
              {savings > 0 && (
                <span className="font-sans text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: '#000', color: '#fff' }}>
                  SAVE {formatPrice(savings)} ({discountPct}%)
                </span>
              )}
            </div>

            <div className="-mx-5 px-5 pt-4 mt-4 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
              <button data-testid={`add-bundle-${bundle.id}`} onClick={() => handleAddBundle(bundle)}
                disabled={isAdding || checkedProducts.length === 0 || !allVariantsSelected}
                className="w-full h-12 font-sans text-sm font-bold uppercase tracking-wider rounded-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                style={{
                  backgroundColor: isAdded ? '#16a34a' : (!allVariantsSelected ? '#888' : '#000'),
                  color: '#fff',
                  opacity: checkedProducts.length === 0 ? 0.4 : 1,
                }}>
                {isAdding ? <Loader2 size={16} className="animate-spin" /> :
                 isAdded ? <><Check size={16} /> Added!</> :
                 !allVariantsSelected ? 'Select All Options' :
                 <><ShoppingBag size={16} /> Add All to Cart {savings > 0 ? `— Save ${discountPct}%` : ''}</>}
              </button>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
